/**
 * ConfigMediaPage
 *
 * Admin page to configure the CDN used for media serving for this org.
 * Reads/writes organisation/{org_id}/config/media in Firestore via
 * the /admin/config/media endpoints.
 *
 * Storage adapter selection is in Settings > Configuration > Storage.
 *
 * Route: /settings/config/media  (admin-only)
 */

import { useCallback, useEffect, useState } from "react";
import useFeedback from "../../hooks/useFeedback";
import SettingsPageShell from "../../components/Settings/SettingsPageShell";
import { PrimaryButton } from "../../components/Button/Buttons";
import Input from "../../components/Inputs/Input";
import Loader from "../../components/Loader/Loader";
import { getServiceConfig, updateServiceConfig } from "../../api/adminConfig";
import { AuthError } from "../../api";
import UIStore from "../../store/uiStore";
import { isDirty } from "../../utils/isDirty";

const EMPTY_FORM = {
  cdn_base_url: "",
};

const ConfigMediaPage = () => {
  const { showFeedback } = useFeedback();
  const orgAvailable = UIStore((s) => s.orgAvailable);

  const [loading, setLoading]     = useState(true);
  const [saving, setSaving]       = useState(false);
  const [form, setForm]           = useState(EMPTY_FORM);
  const [originalForm, setOriginalForm] = useState(EMPTY_FORM);

  const fetchConfig = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getServiceConfig("media");
      if (data.configured) {
        const loaded = { cdn_base_url: data.cdn_base_url ?? "" };
        setForm(loaded);
        setOriginalForm(loaded);
      } else {
        setForm(EMPTY_FORM);
        setOriginalForm(EMPTY_FORM);
      }
    } catch (err) {
      if (!(err instanceof AuthError)) showFeedback("Failed to load media config.", "error");
    } finally {
      setLoading(false);
    }
  }, [showFeedback]);

  useEffect(() => {
    if (orgAvailable) fetchConfig();
  }, [orgAvailable, fetchConfig]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateServiceConfig("media", form);
      setOriginalForm(form);
      showFeedback("Media config saved.", "success");
    } catch (err) {
      showFeedback(err?.response?.data?.error || "Failed to save media config.", "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <SettingsPageShell
      title="Media Configuration"
      description="Configure the CDN used to serve media files. Storage adapter is configured under Storage."
    >
      {loading ? (
        <div className="flex justify-center py-16">
          <Loader />
        </div>
      ) : (
        <div className="flex flex-col gap-6 max-w-lg">
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-medium text-ink-gray-700 dark:text-ink-gray-300">
              CDN Base URL
            </label>
            <Input
              type="url"
              placeholder="https://cdn.example.com"
              value={form.cdn_base_url}
              onChange={(e) => setForm((prev) => ({ ...prev, cdn_base_url: e.target.value }))}
            />
            <p className="text-[11px] text-ink-gray-400 dark:text-ink-gray-500">
              Leave blank to serve media directly from storage.
            </p>
          </div>

          <div className="flex items-center gap-3 pt-2">
            <PrimaryButton onClick={handleSave} disabled={saving || !isDirty(form, originalForm)}>
              {saving ? "Saving…" : "Save"}
            </PrimaryButton>
          </div>
        </div>
      )}
    </SettingsPageShell>
  );
};

export default ConfigMediaPage;

